import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StoreEndpointsConstant } from '@constants/store-endpoints.constant';
import {
  CountryItem,
  OrderActionParams,
  OrderCenterResponse,
  UserAsset,
  UserAssetData,
  UserAssetsApiParams,
  UserOrderData
} from '@models/dashboard.model';
import { CountriesService } from '@services/countries/countries.service';
import { requestTrackingContext } from '@services/requests-tracker/requests-tracker.service';
import { UrlUtils } from '@utils/url.utils';
import { Observable, map } from 'rxjs';
import { dataLoaderContext } from 'tangoe-design-system';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(
    private http: HttpClient,
    private url_utl: UrlUtils,
    private countries_svc: CountriesService
  ) {}

  orderCenterActions(params?: OrderActionParams, key?: string): Observable<OrderCenterResponse> {
    return this.http.get<OrderCenterResponse>(StoreEndpointsConstant.ORDER_TYPES, {
      params: { ...params },
      context: dataLoaderContext(key ?? 'order-center-actions')
    });
  }

  userCountryList(key?: string): Observable<CountryItem[]> {
    return this.countries_svc.getCountries(key).pipe(
      map((data) => {
        if (!data?.countries.length) return [];
        let default_country: CountryItem | undefined = undefined;
        const countries = data.countries.reduce((acc, country) => {
          if (country.default) default_country = country;
          else acc.push(country);
          return acc;
        }, [] as CountryItem[]);

        const collator: Intl.Collator = new Intl.Collator('en');
        countries.sort((a, b) => collator.compare(a.name, b.name));

        if (default_country) countries.unshift(default_country);

        return countries;
      })
    );
  }

  userAssets(params?: UserAssetsApiParams, key?: string): Observable<UserAssetData> {
    const { index = false, ...rest } = params ?? {};
    let query: string = '';
    for (const [key, value] of Object.entries(rest ?? {})) {
      if (!value) continue;
      if (query) query += '&';
      query += `${key}=${value}`;
    }
    return this.http
      .get<UserAssetData>(StoreEndpointsConstant.USER_ASSETS + (query ? `?${query}` : ''), {
        context: dataLoaderContext(key ?? 'user-assets')
      })
      .pipe(
        map((data: UserAssetData) => {
          if (data.assets)
            data.assets = data.assets.sort((a, b) => (a.display_rank < b.display_rank ? -1 : 1));
          return data;
        })
      );
  }

  lookupDevice(identifier: string, fetch_lines?: boolean): Observable<UserAssetData> {
    let params: HttpParams = new HttpParams().set('identifier', identifier);
    if (fetch_lines) params = params.set('fetchLines', 'true');

    return this.http.get<UserAssetData>(StoreEndpointsConstant.DEVICE_IDENTIFIER, {
      params: params,
      context: dataLoaderContext(fetch_lines ? 'devices-list' : 'lookup-device')
    });
  }

  updatePrimaryLine(id: number): Observable<UserAssetData> {
    return this.http.patch<UserAssetData>(
      this.url_utl.parseURL(StoreEndpointsConstant.UPDATE_PRIMARY_LINE, { id: id }),
      '',
      {
        context: requestTrackingContext(0)
      }
    );
  }

  userOrders(
    search_term: string | undefined,
    filter: string | undefined,
    limit: number = 100,
    offset: number = 10
  ): Observable<UserOrderData> {
    let params: { [key: string]: number | string } = {};

    if (search_term)
      params = {
        searchQuery: search_term
      };

    if (filter)
      params = {
        ...params,
        orderFor: filter
      };

    return this.http.get<UserOrderData>(StoreEndpointsConstant.ORDER_LIST, {
      params: {
        ...params,
        limit: limit,
        offset: offset
      },
      context: dataLoaderContext('user-orders')
    });
  }
}
